<template>
    <div v-if="isLoading">
        {{ $t('template.batch-opening-times.loading') }}
    </div>
    <div v-else>
        <div v-if="showHeader" class="row">
            <div class="col-10">
                <h3>{{ $t('template.batch-opening-times.title') }}</h3>
            </div>
            <div class="col-2 text-right">
                <button class="btn btn-link p-0" @click="$emit('close')" :title="$t('template.batch-opening-times.close-button')"><h2>&times;</h2></button>
            </div>
        </div>

        <div class="card my-2">
            <div class="row">
                <div class="col-6">
                    <div class="card-body">
                        <strong class="card-title">{{ $t('template.batch-opening-times.locations-title') }}</strong>
                        <table v-for="locationType in locationTypes" class="table table-sm table-striped col-4">
                            <tr v-for="isActive in ['1', '0']" v-if="getCount(locationsByType[locationType.id], 'active', isActive)">
                                <td>
                                    {{locationType.display_name}}
                                    <span v-if="isActive === '1'">({{ $t('template.batch-opening-times.active') }})</span>
                                    <span v-else>({{ $t('template.batch-opening-times.inactive') }})</span>
                                </td>
                                <td class="col-3">{{ getCount(locationsByType[locationType.id], 'active', isActive) }}</td>
                            </tr>
                        </table>
                    </div>
                </div>
                
                <div class="col-6">
                    <div class="card-body">
                        <strong class="card-title">{{ $t('template.batch-opening-times.service-type') }}</strong>
                        <select v-model="serviceTypeIdentifier" class="form-control form-control-sm">
                            <option v-for="serviceType in serviceTypes" :value="serviceType.identifier">{{serviceType.display_name}}</option>
                        </select>
                        <b-checkbox switch v-model="overwriteConflictingServiceTimes" id="overwriteConflictingServiceTimes" :value="true" :unchecked-value="false" class="d-inline-block mr-1"/>
                        <label for="overwriteConflictingServiceTimes" class="form-check-label line-height-200">{{ $t('template.batch-opening-times.overwrite-conflicts') }}</label>
                    </div>
                </div>
            </div>
        </div>

        <div class="card mb-2">
            <div class="card-body">
                <span v-if="!isSaved">
                    <strong class="card-title">{{ $t('template.batch-opening-times.add-service-time-title') }}</strong><br/>
                    <small v-if="overwriteConflictingServiceTimes"><em>
                        <strong>{{ $t('template.batch-opening-times.watch-out') }}</strong> {{ $t('template.batch-opening-times.location-overwrite-state-on-prefix') }} <strong class="text-danger">{{ $t('template.batch-opening-times.location-overwrite-state-on') }}</strong>.
                    </em></small>
                    <small v-else><em>
                        <strong>{{ $t('template.batch-opening-times.watch-out') }}</strong> {{ $t('template.batch-opening-times.location-overwrite-state-off-prefix') }} <strong>{{ $t('template.batch-opening-times.location-overwrite-state-off') }}</strong>.
                    </em></small>
                </span>
                <strong v-else class="card-title">{{ $t('template.batch-opening-times.service-time-added') }}</strong>

                    <form @submit.prevent="save">
                        <div class="row my-2">
                            <div class="col">{{ $t('template.batch-opening-times.date') }}</div>
                            <div class="col pl-1">{{ $t('template.batch-opening-times.status') }}</div>
                            <div v-if="!useCutOff" class="col pl-1">{{ $t('template.batch-opening-times.from') }}</div>
                            <div v-if="!useCutOff" class="col pl-1">{{ $t('template.batch-opening-times.to') }}</div>
                            <div v-if="useCutOff" class="col pl-1">{{ $t('template.batch-opening-times.cutoff') }}</div>
                            <div class="col pl-1">{{ $t('template.batch-opening-times.description') }}</div>
                            <div class="col pl-1"></div>
                        </div>
                        <location-service-time-form v-model="newServiceTimeByDate" :date="true" :create="true"
                                                    :use-cut-off="!!useCutOff"
                                                    :read-only="isSaving || isSaved"
                                                    @validated="(...args) => this.isNewServiceTimeValid = args[0]"
                        >
                            <button v-if="!isSaved"
                                    type="submit" class="btn btn-sm w-100" :disabled="!isNewServiceTimeValid || isSaving"
                                    :title="addServiceTimeTooltip"
                                    :class="{
                                        'btn-outline-secondary': !isNewServiceTimeValid,
                                        'btn-success': isNewServiceTimeValid,
                                    }"
                            >{{ $t('template.batch-opening-times.save') }}
                                <span v-if="isSaving">
                                    <font-awesome-icon icon="spinner" spin/>
                                </span>
                            </button>
                        </location-service-time-form>

                        <button v-if="isSaved" type="button" class="btn btn-primary mt-4" @click="resetForm">{{ $t('template.batch-opening-times.add-another') }}</button>
                    </form>
            </div>

            <div v-if="successCount" class="alert alert-success">
                {{ $t('template.batch-opening-times.count-added') }} {{successCount}}
            </div>
            <div v-if="generalError" class="alert alert-danger">
                {{ $t('template.batch-opening-times.count-ignored') }} {{generalError}}
            </div>
            <div v-if="locationErrorList.length" class="alert alert-danger">
                {{ $t('template.batch-opening-times.list-errors') }}
                <ul class="list-unstyled">
                    <li v-for="error in locationErrorList" class="">
                        <router-link tag="a" target="_blank" :to="{
                            name:'locationEditServiceTimes',
                            params: {
                                locationTypeIdentifier: locationTypes[error.location.location_type_id].identifier,
                                locationIdentifier: error.location.identifier,
                                serviceTypeIdentifier: serviceTypeIdentifier
                            },
                        }">
                            {{error.location.display_name}}
                        </router-link>:
                        {{error.message}}
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>
import _ from "lodash"
import LocationServiceTimeForm from "../Forms/LocationServiceTimeForm";
import i18n from "../Index.i18n";

export default {
    name: "BatchAddOpeningTimes",
    components: {LocationServiceTimeForm},
    i18n,
    props: {
        selectedResults: {required: true, type: Array},
        showHeader: {required: false, default: false},
    },

    data() {
        return {
            isLoading: true,
            isSaving: false,
            isSaved: false,
            locationTypes: {},
            serviceTypes: [],
            isNewServiceTimeValid: false,
            serviceTypeIdentifier: 'open',
            overwriteConflictingServiceTimes: false,
            newServiceTimeByDate: {
                date: '',
                // week_day: '',
                is_available: '',
                from_time: '',
                to_time: '',
                description: '',
                priority: 0,
            },
            successCount: 0,
            locationErrorList: [],
            generalError: '',
        }
    },

    computed: {
        locationsByType() {
            return _.groupBy(this.selectedResults, 'location_type_id')
        },
        locationsByIdentifier() {
            return _.keyBy(this.selectedResults, 'identifier')
        },
        addServiceTimeTooltip() {
            return this.isNewServiceTimeValid
                ? this.$t('template.batch-opening-times.add-service-time-tooltip-valid')
                : this.$t('template.batch-opening-times.add-service-time-tooltip-invalid')
        },
        selectedServiceType() {
            return this.serviceTypes.find(t => t.identifier === this.serviceTypeIdentifier)
        },
        useCutOff() {
            return this.selectedServiceType?.is_cutoff ?? false
        },
    },

    mounted() {
        this.getLocationDataTypes()
            .then(() => this.isLoading = false)
    },

    methods: {
        getLocationDataTypes() {
            return this.$store.getters.getLocationDataTypes
                .then(locationDataTypes => {
                    this.locationTypes = _.keyBy(this.$store.state.locationDataTypes.locationTypes, 'id')
                    this.serviceTypes = _.orderBy(this.$store.state.locationDataTypes.serviceTypes, 'gui_sort_order')
                })
        },
        getCount(locations, column, value) {
            if (!locations) return 0

            return locations.filter(location => location[column] === value).length
        },
        save() {
            if (this.isSaving) {
                return
            }

            this.isSaving = true
            this.successCount = 0
            this.locationErrorList = []
            this.generalError = ''
            let postData = {
                location_identifiers: [],
                service_times: [
                    _.clone(this.newServiceTimeByDate),
                ],
                overwrite_conflicting_service_times: this.overwriteConflictingServiceTimes,
            }
            _.each(this.locationsByType, (locations, typeId) => {
                postData.location_identifiers = locations.map(location => location.identifier)

                this.$http.post(`/api/rest/locations-batch/${this.locationTypes[typeId].identifier}/service-times/${this.serviceTypeIdentifier}`, postData)
                    .then(response => {
                        this.isSaved = true

                        if (response.status === 207) {
                            _.each(response.data, (responsesForStatus, status) => {
                                _.each(responsesForStatus, responseData => {
                                    let location = null

                                    switch (status) {
                                        case '200':
                                        case '201':
                                        case '204':
                                            this.successCount++
                                            break

                                        default:
                                            location = this.locationsByIdentifier[responseData['location_identifier']]
                                            this.locationErrorList.push({
                                                location: {
                                                    location_type_id: location.location_type_id,
                                                    display_name: location.display_name,
                                                    identifier: responseData['location_identifier'],
                                                },
                                                message: responseData['errors'][0].message,
                                            })
                                            break
                                    }
                                })
                            })
                        }
                        else {
                            this.successCount = response.data.length
                        }
                    })
                    .catch(e => {
                        this.generalError = e.message
                    })
                    .then(() => {
                        this.isSaving = false

                        if (this.successCount === 0 && !this.overwriteConflictingServiceTimes) {
                            this.generalError = this.$t('template.batch-opening-times.message-updated-none')
                        }
                    })
            })
        },
        resetForm() {
            this.isSaved = false

            this.successCount = 0
            this.locationErrorList = []
            this.generalError = ''

            this.newServiceTimeByDate.date = ''
            this.newServiceTimeByDate.is_available = ''
            this.newServiceTimeByDate.from_time = ''
            this.newServiceTimeByDate.to_time = ''
            this.newServiceTimeByDate.description = ''
        },
    },
}
</script>

<style scoped lang="scss">
.line-height-200 {
    line-height: 200%;
}
</style>
